import { CHIME_ATTACK_SECONDS, CHIME_NOTE_SECONDS, CHIME_NOTES, CHIME_PEAK_GAIN } from "./constants";

type AudioContextConstructor = new () => AudioContext;

const audioContextConstructor = (): AudioContextConstructor | null => {
  const scope = globalThis as { AudioContext?: AudioContextConstructor; webkitAudioContext?: AudioContextConstructor };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
};

const playNotes = (context: AudioContext) => {
  const now = context.currentTime;
  for (const note of CHIME_NOTES) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = now + note.start;
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(note.frequency, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(CHIME_PEAK_GAIN, start + CHIME_ATTACK_SECONDS);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + CHIME_NOTE_SECONDS);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + CHIME_NOTE_SECONDS);
  }
};

/**
 * Builds the support reply chime: a soft two-tone sound synthesized with
 * WebAudio, so there is no asset to load. One `AudioContext` is made on the
 * first play and reused. Plays at most once per `throttleMs`. Anything the
 * browser refuses (no WebAudio, a context it won't resume without a gesture)
 * is swallowed: a missing chime is never worth an error.
 *
 * @param throttleMs - The quiet window after a chime.
 * @param now - Clock, injectable for tests.
 * @returns `play`, which returns whether it tried to sound (false when throttled or unsupported).
 */
export const createChime = (throttleMs: number, now: () => number = Date.now) => {
  let context: AudioContext | null = null;
  let lastPlayedAt = Number.NEGATIVE_INFINITY;

  const play = (): boolean => {
    const at = now();
    if (at - lastPlayedAt < throttleMs) return false;
    const AudioContextImpl = audioContextConstructor();
    if (!AudioContextImpl) return false;
    lastPlayedAt = at;
    try {
      context ??= new AudioContextImpl();
      const ready = context;
      if (ready.state === "running") playNotes(ready);
      else
        void ready
          .resume()
          .then(() => playNotes(ready))
          .catch(() => {});
      return true;
    } catch {
      return false;
    }
  };

  return { play };
};
